import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@1trading/react';
import { Ban, Hand, Info, Undo2, WandSparkles } from 'lucide-react';
import { type FieldValueRow, FieldValueTable } from '@/components/field-value-table';
import type { ParkedDeposit } from '@/services/api/parked-deposits';
import { stringifyValue } from '@/utils/format';
import { formatParkedTimestamp } from './format';

const DATE_FIELDS = ['parked_at', 'bcb_completed_at', 'exchange_processed_at'];

const formatFieldValue = (field: string, value: unknown): string => {
  if (DATE_FIELDS.includes(field)) return formatParkedTimestamp(value);
  return stringifyValue(value);
};

interface ParkedDepositDetailDialogProps {
  deposit: ParkedDeposit;
  onClose: () => void;
  onAutoAssign: (deposit: ParkedDeposit) => void;
  onRevert: (deposit: ParkedDeposit) => void;
  onIgnore: (deposit: ParkedDeposit) => void;
  onManualAssign: (deposit: ParkedDeposit) => void;
}

export const ParkedDepositDetailDialog = ({ deposit, onClose, onAutoAssign, onRevert, onIgnore, onManualAssign }: ParkedDepositDetailDialogProps) => {
  // Every non-null field of the record renders (records may carry extra fields)
  const fields = Object.entries(deposit).filter(([, value]) => value !== null && value !== undefined);
  const rows: FieldValueRow[] = fields.map(([field, value]) => ({ key: field, label: field, value: formatFieldValue(field, value) }));

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="flex max-h-[90vh] w-[90vw] flex-col sm:max-w-[1000px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 break-all">
            <Info className="size-5 shrink-0" />
            Parked Deposit Details: {deposit.transaction_id}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
          <FieldValueTable labelVariant="code" rows={rows} />
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <Button className="gap-2" onClick={() => onAutoAssign(deposit)} type="button">
              <WandSparkles className="size-4" />
              Auto Assign
            </Button>
            <Button
              className="gap-2 border-amber-500/50 text-amber-600 hover:bg-amber-500/10 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-400"
              onClick={() => onRevert(deposit)}
              type="button"
              variant="outline"
            >
              <Undo2 className="size-4" />
              Revert
            </Button>
            <Button className="gap-2" onClick={() => onIgnore(deposit)} type="button" variant="destructive">
              <Ban className="size-4" />
              Ignore
            </Button>
            <Button className="gap-2" onClick={() => onManualAssign(deposit)} type="button" variant="secondary">
              <Hand className="size-4" />
              Manual Assign
            </Button>
          </div>
          {/* Close never refetches (legacy parity) */}
          <Button onClick={onClose} type="button" variant="outline">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
ParkedDepositDetailDialog.displayName = 'ParkedDepositDetailDialog';
